import { createRoot } from 'react-dom/client'
import { useState, useEffect, Suspense, useRef, Component } from 'react'
import { Loader, useProgress } from '@react-three/drei'
import './styles.css'
import App from './App'
import ProjectsOverlay from './ProjectsOverlay'
import { Overlay as AudioOverlay } from './Overlay'

// Error boundary to catch and display errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#000',
          color: '#fff',
          fontFamily: 'Gohu, monospace',
          padding: '20px',
          boxSizing: 'border-box'
        }}>
          <h1 style={{ marginBottom: '20px' }}>Something went wrong</h1>
          <pre style={{
            backgroundColor: '#111',
            padding: '20px',
            borderRadius: '5px',
            maxWidth: '80%',
            overflow: 'auto'
          }}>
            {this.state.error?.message || 'Unknown error'}
          </pre>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              backgroundColor: '#333',
              color: '#fff',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontFamily: 'inherit'
            }}
          >
            Reload Page
          </button>
        </div>
      )
    }

    return this.props.children
  }
}

// Custom loader component with text on top, progress bar below
function CustomLoader({ onLoadComplete }) {
  const { active, progress, errors, item, loaded, total } = useProgress()
  const [dots, setDots] = useState('')
  const [fadeOut, setFadeOut] = useState(false)
  
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.')
    }, 400)
    return () => clearInterval(interval)
  }, [])
  
  useEffect(() => {
    // When loading is complete, start fade out
    if (!active && loaded === total && total > 0) {
      setTimeout(() => {
        setFadeOut(true)
        // Notify parent that loading is complete
        setTimeout(() => {
          onLoadComplete && onLoadComplete()
        }, 500) // Wait for fade out to complete
      }, 200) // Small delay before starting fade
    }
  }, [active, loaded, total, onLoadComplete])
  
  // Hide completely after fade out
  if (fadeOut && !active) {
    return null
  }
  
  return (
    <div style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '20px',
      zIndex: 1000,
      opacity: fadeOut ? 0 : 1,
      transition: 'opacity 0.5s ease-out',
      pointerEvents: 'none'
    }}>
      <div style={{
        color: '#ffffff',
        fontFamily: 'Gohu, monospace',
        fontSize: '18px',
        letterSpacing: '0.05em',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        Brewing <span style={{ fontSize: '28px' }}>{`\uEC15`}</span>{dots}
      </div>
      
      <div style={{
        width: '200px',
        height: '2px',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '2px',
        overflow: 'hidden',
        position: 'relative'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: `${progress}%`,
          backgroundColor: '#ffffff',
          transition: 'width 0.3s ease',
          borderRadius: '2px'
        }} />
      </div>
    </div>
  )
}

function Overlay({ showProjectsOverlay, setShowProjectsOverlay }) {
  const [showUI, setShowUI] = useState(false)  // Master control for all UI
  const [showStudioInfo, setShowStudioInfo] = useState(false)
  const [hideStudioInfo, setHideStudioInfo] = useState(false)
  const [visibleChars, setVisibleChars] = useState(0)
  const [showTitle, setShowTitle] = useState(false)
  const [showFirstPara, setShowFirstPara] = useState(false)
  const [showSecondPara, setShowSecondPara] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [isMobileLandscape, setIsMobileLandscape] = useState(false)
  const [animationComplete, setAnimationComplete] = useState(false)  // Track when text animation is done

  const studioText = "Coffee Coded is a dev studio and collective of seasoned developers specialising in app development, generative AI and web3."
  const techText = "We leverage cutting-edge tech for diverse clients across Web2 and Web3."

  useEffect(() => {
    const checkMobile = () => {
      const width = window.innerWidth
      const height = window.innerHeight
      const mobile = width <= 768
      const landscape = mobile && width > height
      setIsMobile(mobile)
      setIsMobileLandscape(landscape)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    window.addEventListener('orientationchange', checkMobile)
    return () => {
      window.removeEventListener('resize', checkMobile)
      window.removeEventListener('orientationchange', checkMobile)
    }
  }, [])
  
  useEffect(() => {
    // Global click handler to fade studio text when clicking anywhere
    const handleGlobalClick = (e) => {
      // Only hide if studio text is currently visible, not already hiding, AND animation is complete
      if (showStudioInfo && !hideStudioInfo && showUI && animationComplete) {
        // Don't hide if clicking on UI buttons or interactive elements
        if (!e.target.closest('button') && !e.target.closest('a') && !e.target.closest('[data-audio-button]')) {
          setHideStudioInfo(true)
        }
      }
    }

    // Add click listener to document
    document.addEventListener('click', handleGlobalClick)

    return () => {
      document.removeEventListener('click', handleGlobalClick)
    }
  }, [showStudioInfo, hideStudioInfo, showUI, animationComplete])
  
  useEffect(() => {
    // Sequence: Scene loads (1.5s) -> UI fades in (2.5s) -> Studio text appears (4s)
    const uiTimer = setTimeout(() => {
      setShowUI(true)
    }, 2500) // Start fading in UI after scene is visible (0.5s loading fade + 1.5s scene fade + 0.5s delay)
    
    // Then start studio text animation
    const studioTimer = setTimeout(() => {
      setShowStudioInfo(true)
    }, 4000) // 4s delay - text animation starts after everything else has settled
    
    return () => {
      clearTimeout(uiTimer)
      clearTimeout(studioTimer)
    }
  }, [])
  
  useEffect(() => {
    if (showStudioInfo && !hideStudioInfo) {
      // Staggered start for each section's typing animation
      const timeouts = []
      const intervals = []
      let mounted = true
      
      // Title appears immediately
      timeouts.push(setTimeout(() => mounted && setShowTitle(true), 0))
      
      // Start typing first paragraph after 175ms
      timeouts.push(setTimeout(() => {
        if (!mounted) return
        setShowFirstPara(true)
        let firstParaChar = 0
        const firstParaInterval = setInterval(() => {
          if (!mounted) {
            clearInterval(firstParaInterval)
            return
          }
          firstParaChar += 2
          if (firstParaChar >= studioText.length) {
            clearInterval(firstParaInterval)
            setVisibleChars(studioText.length)
          } else {
            setVisibleChars(firstParaChar)
          }
        }, 15)
        intervals.push(firstParaInterval)
      }, 175))
      
      // Start typing second paragraph after first completes
      timeouts.push(setTimeout(() => {
        if (!mounted) return
        setShowSecondPara(true)
        let secondParaChar = studioText.length
        const secondParaInterval = setInterval(() => {
          if (!mounted) {
            clearInterval(secondParaInterval)
            return
          }
          secondParaChar += 2
          if (secondParaChar >= studioText.length + techText.length) {
            clearInterval(secondParaInterval)
            setVisibleChars(studioText.length + techText.length)
            // Mark animation as complete when all text is visible
            setAnimationComplete(true)
          } else {
            setVisibleChars(secondParaChar)
          }
        }, 15)
        intervals.push(secondParaInterval)
      }, 350 + (studioText.length * 7)))
      
      return () => {
        mounted = false
        timeouts.forEach(timeout => clearTimeout(timeout))
        intervals.forEach(interval => clearInterval(interval))
      }
    } else if (hideStudioInfo) {
      // Reverse all animations
      setShowTitle(false)
      setShowFirstPara(false)
      setShowSecondPara(false)
      setAnimationComplete(false)  // Reset animation state

      const reverseInterval = setInterval(() => {
        setVisibleChars(prev => {
          const newVal = Math.max(0, prev - 4)
          if (newVal === 0) {
            clearInterval(reverseInterval)
          }
          return newVal
        })
      }, 10)
      
      return () => clearInterval(reverseInterval)
    }
  }, [showStudioInfo, hideStudioInfo, studioText, techText])
  
  const handleStudioClick = () => {
    // Only allow click to hide if animation is complete
    if (!hideStudioInfo && animationComplete) {
      setHideStudioInfo(true)
    } else if (hideStudioInfo) {
      // Reset to show again
      setHideStudioInfo(false)
      setShowStudioInfo(true)
      setVisibleChars(0)
      setShowTitle(false)
      setShowFirstPara(false)
      setShowSecondPara(false)
      setAnimationComplete(false)  // Reset animation state
    }
  }
  
  return (
    <div style={{ 
      position: 'absolute', 
      top: 0, 
      left: 0, 
      width: '100%', 
      height: '100%',
      pointerEvents: 'none'
    }}>
      <div style={{
        position: 'absolute',
        top: isMobileLandscape ? 8 : (isMobile ? 15 : 40),
        left: isMobileLandscape ? 8 : (isMobile ? 15 : 40),
        fontSize: isMobileLandscape ? '12px' : (isMobile ? '12px' : '18px'),
        fontFamily: 'Gohu, monospace',
        letterSpacing: '1px',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        opacity: showUI ? 1 : 0,
        transition: 'opacity 1.5s ease-in-out',
        pointerEvents: 'none'
      }}>
        COFFEE CODED <span style={{ fontSize: isMobileLandscape ? '17px' : (isMobile ? '18px' : '28px') }}>{`\uEC15`}</span>
      </div>
      
      {/* Build with us button - Top Right */}
      <a
        href="mailto:coffeecoded@gmail.com?subject=Let's build a new project!"
        style={{
          position: 'absolute',
          top: isMobileLandscape ? 8 : (isMobile ? 15 : 40),
          right: isMobileLandscape ? 8 : (isMobile ? 15 : 40),
          fontFamily: 'Gohu, monospace',
          fontSize: isMobileLandscape ? '10px' : (isMobile ? '10px' : '14px'),
          color: '#ffffff',
          background: 'transparent',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          padding: isMobileLandscape ? '5px 9px' : (isMobile ? '5px 10px' : '8px 16px'),
          textDecoration: 'none',
          cursor: 'pointer',
          transition: 'all 0.3s ease, opacity 1.5s ease-in-out',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
          opacity: showUI ? 1 : 0,
          pointerEvents: showUI ? 'auto' : 'none',
          zIndex: 10,
          display: 'inline-block'
        }}
        onMouseEnter={(e) => {
          e.target.style.background = 'rgba(255, 255, 255, 0.1)'
          e.target.style.borderColor = 'rgba(255, 255, 255, 0.6)'
        }}
        onMouseLeave={(e) => {
          e.target.style.background = 'transparent'
          e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)'
        }}
      >
        Build with Us
      </a>
      
      {/* Studio Info - Top Left (Below Coffee Coded) */}
      <div
        onClick={handleStudioClick}
        style={{
          position: 'absolute',
          // Position below COFFEE CODED title with same left margin
          top: isMobileLandscape ? 35 : (isMobile ? 50 : 80),
          left: isMobileLandscape ? 8 : (isMobile ? 15 : 40),
          right: isMobileLandscape ? '40%' : (isMobile ? '20%' : 'auto'),
          fontFamily: 'Gohu, monospace',
          fontSize: isMobileLandscape ? '11px' : (isMobile ? '11px' : '14px'),
          lineHeight: '1.6',
          color: '#ffffff',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
          maxWidth: isMobileLandscape ? 'calc(60% - 8px)' : (isMobile ? 'calc(80% - 15px)' : '350px'),
          opacity: hideStudioInfo ? 0 : (showStudioInfo && showUI ? 1 : 0),
          transform: showStudioInfo && !hideStudioInfo ? 'translateX(0)' : 'translateX(-10px)',
          transition: 'all 2s cubic-bezier(0.4, 0, 0.2, 1)',
          cursor: hideStudioInfo ? 'default' : (animationComplete ? 'pointer' : 'default'),
          pointerEvents: hideStudioInfo || !showUI ? 'none' : 'auto',
          zIndex: 10
        }}>
        <div style={{ 
          marginBottom: '12px',
          fontSize: '14px',
          fontWeight: 'bold',
          letterSpacing: '2px',
          opacity: showTitle && !hideStudioInfo ? 1 : 0,
          transform: showTitle && !hideStudioInfo ? 'translateY(0)' : 'translateY(10px)',
          transition: 'all 2s cubic-bezier(0.19, 1, 0.22, 1)' // easeOutCubic
        }}>
          The Studio
        </div>
        <div style={{ 
          // Fade in gradually as text appears
          opacity: showFirstPara && !hideStudioInfo ? Math.min(0.9, (visibleChars / studioText.length) * 1.2) : 0,
          transform: showFirstPara && !hideStudioInfo ? 'translateY(0)' : 'translateY(10px)',
          transition: 'transform 2s cubic-bezier(0.19, 1, 0.22, 1)', // Only transition transform, not opacity
          marginBottom: '8px',
          minHeight: '48px' // Reserve space to prevent layout shift
        }}>
          {studioText.substring(0, Math.min(visibleChars, studioText.length))}
        </div>
        <div style={{ 
          // Fade in gradually as text appears
          opacity: showSecondPara && !hideStudioInfo ? 
            Math.min(0.7, Math.max(0, (visibleChars - studioText.length) / techText.length) * 1) : 0,
          transform: showSecondPara && !hideStudioInfo ? 'translateY(0)' : 'translateY(10px)',
          transition: 'transform 2s cubic-bezier(0.19, 1, 0.22, 1)', // Only transition transform, not opacity
          minHeight: '24px' // Reserve space to prevent layout shift
        }}>
          {visibleChars > studioText.length ? 
            techText.substring(0, Math.min(visibleChars - studioText.length, techText.length)) : 
            ''
          }
        </div>
      </div>
      
      {/* Our Projects button - Bottom Right */}
      <button
        onClick={() => setShowProjectsOverlay(true)}
        style={{
          position: 'absolute',
          bottom: isMobileLandscape ? 8 : (isMobile ? 15 : 40),
          right: isMobileLandscape ? 8 : (isMobile ? 15 : 40),
          fontFamily: 'Gohu, monospace',
          fontSize: isMobileLandscape ? '10px' : (isMobile ? '10px' : '14px'),
          color: '#ffffff',
          background: 'transparent',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          padding: isMobileLandscape ? '5px 9px' : (isMobile ? '5px 10px' : '8px 16px'),
          textDecoration: 'none',
          cursor: 'pointer',
          transition: 'all 0.3s ease, opacity 1.5s ease-in-out',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
          opacity: showUI ? 1 : 0,
          pointerEvents: showUI ? 'auto' : 'none',
          zIndex: 10
        }}
        onMouseEnter={(e) => {
          e.target.style.background = 'rgba(255, 255, 255, 0.1)'
          e.target.style.borderColor = 'rgba(255, 255, 255, 0.6)'
        }}
        onMouseLeave={(e) => {
          e.target.style.background = 'transparent'
          e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)'
        }}
      >
        Our Experience
      </button>
      
      {/* <div style={{ position: 'absolute', bottom: 40, right: 40, fontSize: '13px' }}>2025</div> */}
    </div>
  )
}

function RootApp() {
  const [showProjectsOverlay, setShowProjectsOverlay] = useState(false)
  const [webglError, setWebglError] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const projectsRef = useRef(null)

  useEffect(() => {
    // Detect if we're starting in mobile view (important for refresh scenarios)
    const isMobileView = window.innerWidth <= 768 ||
                         (window.navigator.userAgent.includes('Mobile') && window.innerWidth <= 1024)

    // Longer delay for mobile view to ensure proper WebGL initialization
    const initDelay = isMobileView ? 200 : 100

    const timer = setTimeout(() => {
      setIsReady(true)
    }, initDelay)

    // Check for WebGL support
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
    if (!gl) {
      setWebglError(true)
      console.error('WebGL not supported')
    }

    // Handle WebGL context loss
    const handleContextLost = (e) => {
      console.error('WebGL context lost', e)
      e.preventDefault()
      setTimeout(() => {
        window.location.reload()
      }, 1500)
    }

    // Handle page visibility change to prevent WebGL issues
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('Page hidden')
      } else {
        console.log('Page visible')
        // Don't force re-render on visibility change anymore
        // This was causing reloads when switching tabs
      }
    }

    // Handle viewport/resize changes (especially for mobile simulation)
    const handleResize = () => {
      // Only handle significant size changes, not orientation
      // This prevents reloads on rotation
      console.log('Resize event detected')
      // Canvas and Three.js handle resize internally
      // No need to force re-render
    }

    window.addEventListener('webglcontextlost', handleContextLost)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('resize', handleResize)
    window.addEventListener('orientationchange', handleResize)

    return () => {
      clearTimeout(timer)
      window.removeEventListener('webglcontextlost', handleContextLost)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleResize)
    }
  }, [])

  if (webglError) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#000',
        color: '#fff',
        fontFamily: 'Gohu, monospace',
        textAlign: 'center',
        padding: '20px'
      }}>
        <div>
          <h1>WebGL Not Available</h1>
          <p>This experience requires WebGL support.</p>
          <p>Please try a different browser or update your graphics drivers.</p>
        </div>
      </div>
    )
  }

  // Don't render until ready to prevent white screen
  if (!isReady) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontFamily: 'Gohu, monospace'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Initializing <span style={{ fontSize: '28px' }}>{`\uEC15`}</span>...
        </div>
      </div>
    )
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <Suspense fallback={
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: '#ffffff',
          fontFamily: 'Gohu, monospace',
          fontSize: '18px',
          letterSpacing: '0.05em',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          Brewing <span style={{ fontSize: '28px' }}>{`\uEC15`}</span>...
        </div>
      }>
        <App key={isReady ? 'ready' : 'loading'} onProjectsClick={() => setShowProjectsOverlay(true)} />
        <CustomLoader onLoadComplete={() => {
          // Loading complete callback can be used here if needed
        }} />
      </Suspense>
      <Overlay showProjectsOverlay={showProjectsOverlay} setShowProjectsOverlay={setShowProjectsOverlay} />
      <AudioOverlay />
      <ProjectsOverlay
        ref={projectsRef}
        isVisible={showProjectsOverlay}
        onClose={() => setShowProjectsOverlay(false)}
      />
    </div>
  )
}

createRoot(document.getElementById('root')).render(
  <ErrorBoundary>
    <RootApp />
  </ErrorBoundary>
)
